import { type ReactElement } from 'react';
import { X } from 'lucide-react';
import {
  canTransition,
  reservationOn,
  zoneName,
  can,
  type DayMode,
  type Membership,
  type MapReservation,
  type MapSpot,
  type MapVenue,
  type FloorStatus,
  type ReservationRequest,
  type SpotStatus,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { DIALOG_BASE, DIALOG_CARD, DIALOG_FULL } from '@/components/map/dialog-frame';
import { ReserveForm } from '@/components/map/reserve-form';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import { useFullScreen } from '@/hooks/use-full-screen';
import { dayMonthOf, moneyOf, timeOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { STATUS_COPY, STATUS_TONE } from './spot-tile';

type Action = { key: CopyKey; status: FloorStatus };

// The moves §7 allows, in the order the floor uses them.
const ACTIONS: Readonly<Record<SpotStatus, readonly Action[]>> = {
  free: [
    { key: 'markOccupied', status: 'occupied' },
    { key: 'markCleaning', status: 'cleaning' },
  ],
  reserved: [
    { key: 'checkIn', status: 'occupied' },
    { key: 'markFree', status: 'free' },
    { key: 'markCleaning', status: 'cleaning' },
  ],
  occupied: [
    { key: 'markFree', status: 'free' },
    { key: 'markCleaning', status: 'cleaning' },
  ],
  cleaning: [
    { key: 'markFree', status: 'free' },
    { key: 'markOccupied', status: 'occupied' },
  ],
};

// A held spot checks in and cancels through its booking, not its status.
export function actionsFor(status: SpotStatus, held = false): readonly Action[] {
  return ACTIONS[status].filter(
    (action) =>
      canTransition(status, action.status) &&
      !(held && status === 'reserved' && action.status !== 'cleaning'),
  );
}

export type BookingAction = 'checkIn' | 'extendHold' | 'cancelRes';

// What the booking on a spot may be asked to do on the day being shown. §10
export function bookingActionsFor(
  booking: MapReservation | null,
  status: SpotStatus | null,
  mode: DayMode,
): readonly BookingAction[] {
  if (booking === null || booking.status !== 'active' || mode === 'past') return [];
  if (status === 'occupied') return [];
  return mode === 'live' ? ['checkIn', 'extendHold', 'cancelRes'] : ['extendHold', 'cancelRes'];
}

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <span className="flex justify-between gap-3 py-1 text-[13px]">
      <span className="text-muted-foreground">{label}</span>
      <b className="text-end font-semibold">{value}</b>
    </span>
  );
}

function Booking({ booking, venue }: { booking: MapReservation; venue: MapVenue }): ReactElement {
  const { t, language } = useTranslation();
  return (
    <div className="flex flex-col rounded-xl border p-3">
      <h4 className="mb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        {t('reservation')}
      </h4>
      <Row label={t('guest')} value={booking.guestName} />
      {booking.guestPhone !== null && <Row label={t('phone')} value={booking.guestPhone} />}
      <Row
        label={t('dates')}
        value={`${dayMonthOf(booking.startsOn, language)} – ${dayMonthOf(booking.endsOn, language)}`}
      />
      <Row
        label={t('heldUntil')}
        value={
          timeOf(booking.holdUntil, venue.timezone, language) +
          (booking.holdFeeCents === 0
            ? ''
            : ` · ${moneyOf(booking.holdFeeCents, venue.currency, language)}`)
        }
      />
      {booking.depositCents > 0 && (
        <Row label={t('deposit')} value={moneyOf(booking.depositCents, venue.currency, language)} />
      )}
      {booking.notes !== null && <Row label={t('notes')} value={booking.notes} />}
    </div>
  );
}

// One dialog per spot, opened by its tile and closed after every action: the
// toast and the pulse are the confirmation (§15). What the spot is for is a
// booking, so the form is what opens; the moves §7 allows sit under it, and
// nobody walks through two dialogs to reserve one umbrella.
export function SpotDialog({
  spot,
  venue,
  mode,
  date,
  today,
  nowMinutes,
  busy,
  membership,
  onClose,
  onStatus,
  onStaffOnly,
  refused,
  onReserve,
  onCancelBooking,
  onCheckIn,
  onExtend,
}: {
  spot: MapSpot;
  venue: MapVenue;
  mode: DayMode;
  date: string;
  today: string;
  nowMinutes: number;
  busy: boolean;
  membership: Membership;
  onClose: () => void;
  onStatus: (status: FloorStatus) => void;
  onStaffOnly: (staffOnly: boolean) => void;
  // §10: a refused booking keeps this screen, and says why on it.
  refused?: string | null;
  onReserve: (request: ReservationRequest) => void;
  onCancelBooking: (booking: MapReservation) => void;
  onCheckIn: (booking: MapReservation) => void;
  onExtend: (booking: MapReservation) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  useScrollLock();
  const full = useFullScreen();
  const status = mode === 'past' ? null : spot.status;
  const booking = reservationOn(spot);
  const zone = venue.zones.find((each) => each.key === spot.zoneKey);
  const zoneLabel = zoneName(zone, language, spot.zoneKey);
  const ACT: Record<BookingAction, (booking: MapReservation) => void> = {
    checkIn: onCheckIn,
    extendHold: onExtend,
    cancelRes: onCancelBooking,
  };

  // Nothing to book where a booking already stands, a checkout is paying for
  // it, or the day is behind us.
  // §4: what the dialog offers is what this member may actually do.
  const owner = can(membership, 'venue:settings');
  const free = !(booking === null && spot.pendingCheckout);
  const bookable =
    mode !== 'past' &&
    booking === null &&
    !spot.pendingCheckout &&
    can(membership, 'reservation:write');
  const moves = mode === 'live' && status !== null && free && can(membership, 'spot:status');
  // Everything the spot can be asked to do that is not the booking form.
  const rest = (
    <>
      {can(membership, 'reservation:write') &&
        bookingActionsFor(booking, status, mode).map((action) => (
          <Button
            key={action}
            size="lg"
            variant={action === 'checkIn' ? 'default' : 'outline'}
            className={cn('h-12 w-full text-[15px]', action === 'cancelRes' && 'text-destructive')}
            disabled={busy}
            onClick={() => ACT[action](booking as MapReservation)}
          >
            {t(action)}
          </Button>
        ))}
      {/* And the moves the floor still allows, beneath what it is for. §7 */}
      {moves &&
        actionsFor(status as SpotStatus, booking !== null).map((action) => (
          <Button
            key={action.key}
            size="lg"
            disabled={busy}
            variant="outline"
            className="h-12 w-full text-[15px]"
            onClick={() => onStatus(action.status)}
          >
            {t(action.key)}
          </Button>
        ))}
      {owner && mode !== 'past' && !(booking === null && spot.pendingCheckout) && (
        <Button
          variant="outline"
          disabled={busy}
          className="w-full"
          onClick={() => onStaffOnly(!spot.staffOnly)}
        >
          {t(spot.staffOnly ? 'allowOnline' : 'makeStaffOnly')}
        </Button>
      )}
      {zone !== undefined && mode !== 'past' && (
        <Row label={t('dayPrice')} value={moneyOf(zone.dayPriceCents, venue.currency, language)} />
      )}
    </>
  );

  const detail = (
    <>
      {/* A checkout holds it already: say so rather than offer a refusal. §13 */}
      {booking === null && spot.pendingCheckout && mode !== 'past' && (
        <p
          role="status"
          className="rounded-xl border border-dashed border-amber-400 bg-amber-50/70 p-3 text-[13px] text-amber-900"
        >
          <b>{t('payingNow')}.</b> {t('payingNote')}
        </p>
      )}
      {mode === 'past' && (
        <div className="flex flex-col rounded-xl border p-3">
          <h4 className="mb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            {t('history')}
          </h4>
          <Row label={t('occupations')} value={String(spot.occupations)} />
          {spot.reservations.map((each) => (
            <Row key={each.id} label={t('guest')} value={each.guestName} />
          ))}
        </div>
      )}
      {booking !== null && mode !== 'past' && <Booking booking={booking} venue={venue} />}
    </>
  );

  return (
    <>
      <button
        type="button"
        aria-label={t('close')}
        onClick={onClose}
        className="fixed inset-0 z-70 cursor-default bg-black/40"
      />
      {/* A screen on a phone and a dialog on a desktop: the frame is fixed and
          only the middle of it scrolls, so nothing under a finger drifts. §20 */}
      <div
        role="dialog"
        aria-label={`${t('spotLabel')} ${spot.number}`}
        className={cn(DIALOG_BASE, full ? DIALOG_FULL : DIALOG_CARD)}
      >
        <div className="mx-auto flex min-h-0 w-full max-w-[560px] flex-1 flex-col">
          <header className="flex shrink-0 items-center gap-3 pb-2.5">
            <span
              className={cn(
                'grid size-12 place-items-center rounded-2xl border-2 text-lg font-extrabold',
                status === null ? 'border-stone-300 bg-stone-100' : STATUS_TONE[status],
              )}
            >
              {spot.number}
            </span>
            <span className="min-w-0">
              <b className="block truncate text-[15px] font-bold">#{spot.number}</b>
              <span className="block truncate text-xs text-muted-foreground">
                {zoneLabel}
                {status !== null && ` · ${t(STATUS_COPY[status])}`}
                {spot.staffOnly && ` · ★ ${t('staffOnly')}`}
              </span>
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="ms-auto"
              aria-label={t('close')}
              onClick={onClose}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </header>

          {/* The booking is what the spot is for, so its form is what opens,
              and Save stays on the screen while the fields scroll past. §10 */}
          {bookable ? (
            <ReserveForm
              venue={venue}
              spots={[spot]}
              date={date}
              today={today}
              nowMinutes={nowMinutes}
              busy={busy}
              className="flex-1"
              refused={refused}
              onSubmit={onReserve}
            >
              {rest}
            </ReserveForm>
          ) : (
            <>
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto overscroll-contain px-1">
                {detail}
              </div>
              <div className="flex shrink-0 flex-col gap-2 pt-2.5">{rest}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
